<template>
  <div>
    <a-drawer
      :zIndex="8990"
      class="view-tour-details"
      wrapClassName="drawer"
      placement="right"
      :afterVisibleChange="loadMap"
      :visible="visible"
      :closable="false"
      @close="onClose"
    >
      <div class="h-full">
        <nav class="flex items-center justify-between">
          <h2 class="text-xl">{{ (tourInfo && tourInfo.name) || '' }}</h2>
          <base-button-icon icon="close" @click="onClose" />
        </nav>
        <hr class="my-4" />
        <div class="w-full flex justify-between flex-wrap lg:flex-nowrap">
          <ul class="lg:pr-4 w-full max-w-lg">
            <li
              :key="index"
              v-for="(key, index) in ['state', 'assigned_to', 'created_at', 'updated_at']"
              class="mb-2 py-2 flex justify-between border-b border-dashed"
            >
              <span class="leading-6 text-base text-secondary-dark">{{ fields[key] }}:</span>
              <span v-if="key === 'created_at' || key === 'updated_at'" class="leading-5 sm:mt-0">{{
                tourInfo && formatDate(tourInfo[key])
              }}</span>
              <span v-else class="leading-5 sm:mt-0">{{ tourInfo && tourInfo[key] }}</span>
            </li>
            <li v-if="tourInfo && tourInfo.state === 'Completed'" class="flex justify-end">
              <base-button :loading="isExporting" @click="exportTour(tourInfo)" size="sm">
                Export Tour
              </base-button>
            </li>
          </ul>
          <BaseMap
            ref="map"
            v-if="loaded"
            v-model="selectedTourItem"
            class="shadow-lg h-60 xl:h-80 xl:w-3/5"
            :groups="[{list: this.tourItems, label: 'TourItem'}]"
            :start-populating="startPopulating"
            :option-type="{value: 'meter_serial_number'}"
          />
        </div>
        <Grid
          class="mt-8"
          :size="'sm'"
          @api="getTourItems()"
          :headers="headers"
          :payload="{results, count, next, previous}"
          @rendered="renderComplete = true"
        >
          <!-- <template #filters>
            <base-input
              v-model.trim="meterSerial"
              class=""
              size="sm"
              label="Meter_Serial"
              clearable
            />

          </template>
          <template >

        </template> -->
          <template #buttons>
            <a-popover
              v-model="filtersVisible"
              placement="bottomLeft"
              trigger="click"
              arrowPointAtCenter
            >
              <span slot="title" class="flex justify-between">
                <span class="text">Filter by State</span>
                <a class="cursor-pointer text-sm" slot="content" @click="itemStateFilter = []"
                  >Clear</a
                ></span
              >
              <base-button
                v-tooltip="{content: showTooltip(itemStateFilter), placement: 'top'}"
                size="sm"
                context="secondary"
                class="h-1/2 self-center mt-3"
              >
                <base-icon icon="filters" class="bg-transparent mr-3 text-base active:border-0" />
                {{ filterButton }}
              </base-button>
              <template slot="content">
                <ListSelect
                  :optionType="{value: 'val', label: 'text'}"
                  :options="readoutStates"
                  :limit="100"
                  :showSelected="true"
                  class="w-full text-sm"
                  v-model="itemStateFilter"
                />
              </template>
            </a-popover>
            <base-input
              v-model.trim="meterSerial"
              class=""
              size="sm"
              label="Meter_Serial"
              clearable
            />
          </template>

          <template #[`body.updated_at`]="{item}">
            <span>
              {{
                item.updated_at
                  ? $d(new Date(item.updated_at), 'long')
                  : $d(new Date(item.created_at), 'long')
              }}
            </span>
          </template>
          <template #[`body.customer_name`]="{item}">
            <div class="flex flex-col">
              <div :class="['flex flex-col lg:flex-row lg:items-center leading-5 font-medium']">
                <span>{{ item.customer_name }} - {{ item.customer_no }}</span>
                <div v-if="item.is_distributor" class="flex-shrink-0 lg:ml-2">
                  <span
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded text-white bg-blue-light block"
                    >{{ $t(`mygwf.components.Grid.distributor`) }}</span
                  >
                </div>
              </div>
              <div class="hidden pt-1 lg:block leading-5 text-secondary-dark">
                {{ Array.from([item.street, item.city, item.zip_code]).filter(Boolean).join(', ') }}
              </div>
            </div>
          </template>

          <template #[`body.meter_serial_number`]="{item}">
            <div :class="['flex flex-col leading-5 font-medium']">
              <span>{{ item.meter_serial_number }}</span>
              <div v-if="item.medium === '07'" class="flex-shrink-0">
                <span
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded text-white bg-blue-light"
                  >Water {{ item.registers[0].obis_code }}</span
                >
              </div>
            </div>
          </template>
          <template #[`body.state`]="{item}">
            <div class="w-24 lg:w-16">
              <span>{{ showDataOrNot(whatStateIs(item.state)) }}</span>
            </div>
          </template>
          <template #[`body.value`]="{item}">
            <div class="flex space-x-8 items-center">
              <base-button-icon
                icon="edit"
                @click="setValue(item)"
                action="edit_volume"
                size="sm"
              />
              <span class="text-left">{{
                item.registers[0].value === null ? '-' : parseFloat(item.registers[0].value)
              }}</span>
            </div>
          </template>
          <template #[`body.details`]="{item}">
            <div class="flex justify-end space-x-4 mr-4">
              <base-button
                @click="showExpanded(item), handlePinClick(item)"
                context="secondary"
                size="sm"
                >Details
              </base-button>
            </div>
          </template>
          <template #expanded="{item}">
            <transition name="slidedown" class="slidedown transition-all duration-75 ease-in-out">
              <div v-if="visibleTourItemDetails.includes(item.uuid)">
                <TourItemDetails :tourItemData="item" />
              </div>
            </transition>
          </template>
        </Grid>
      </div>

      <a-modal
        title="Change volume"
        :zIndex="9991"
        v-model="isModalVisible"
        :confirm-loading="confirmModalLoading"
      >
        <form ref="tourVolumeForm" @submit.prevent="onSubmit">
          <div class="py-3 xl:py-4 xl:flex items-center">
            <span class="xl:w-1/2 leading-5">Volume:</span>
            <base-input
              class="mt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3"
              v-model="volume"
              type="string"
              :focused="() => true"
            />
          </div>
        </form>
        <template slot="footer">
          <!--          <a-button key="back" @click="handleCancel">
            Return
          </a-button>-->
          <div class="flex justify-end">
            <base-button action="close" context="secondary" @click="handleCancel()">
              <base-icon icon="close" />
            </base-button>
            &nbsp;&nbsp;
            <base-button
              action="submit"
              :loading="pendingRequest"
              type="submit"
              context="primary"
              @click="onSubmit()"
            >
              <base-icon icon="checkmark" />
            </base-button>
          </div>

          <!--          <a-button key="submit" type="primary" :loading="loading" >
            Submit
          </a-button>-->
        </template>
      </a-modal>
    </a-drawer>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'

import fileExport from '@/state/helpers/fileExport.js'

import Drawer from 'ant-design-vue/lib/drawer'
import 'ant-design-vue/lib/drawer/style/index.css'

import Modal from 'ant-design-vue/lib/modal'
import 'ant-design-vue/lib/modal/style/index.css'
import Popover from 'ant-design-vue/lib/popover'
import 'ant-design-vue/lib/popover/style/index.css'
import ListSelect from '@/components/shared/ListSelect.vue'

import 'ant-design-vue/lib/card/style/index.css'

import Grid from '@/components/shared/Grid'

import {BaseButtonIcon, BaseButton, BaseMap, BaseIcon, BaseInput} from '@/components/base'
import TourItemDetails from '@/components/pages/tours/TourItemDetails.vue'
import _isEmpty from 'lodash-es/isEmpty'

export default {
  name: 'TourDetailsDrawer',
  props: {
    tourListPath: {
      type: String,
      default: '/tours',
    },
    tour: {
      type: Object,
    },
  },
  components: {
    TourItemDetails,
    'a-drawer': Drawer,
    'a-modal': Modal,
    'a-popover': Popover,
    BaseButtonIcon,
    BaseButton,
    BaseIcon,
    Grid,
    BaseMap,
    BaseInput,
    ListSelect,
  },

  async mounted() {
    this.visible = true
    //console.log('mounted', this.tour)
    if (_isEmpty(this.tourInfo)) {
      this.tourInfo = await this.$store.dispatch('tours/getTour', {
        uuid: this.$route.params.id,
      })
    }
    //await this.getTourItems()
  },
  data() {
    return {
      tourItemList: this.tourItems,
      tourItems: [],
      tourInfo: this.tour,
      meterSerial: this.$route.query.meter_serial || '',
      selectedTourItem: null,
      renderComplete: false,
      startPopulating: false,
      clickedMapPin: null,
      visibleTourItemDetails: [],
      visible: false,
      filtersVisible: false,
      isModalVisible: false,
      confirmModalLoading: false,
      pendingRequest: false,
      volume: '',
      loaded: false,
      itemStateFilter: [],
      tourListUrl: this.tourListPath,
      row: -1,
      fields: {
        state: 'State',
        created_at: 'Imported at',
        assigned_to: 'Assigned to',
        updated_at: 'Updated at',
      },
      readoutStates: [
        {text: 'Not Readout', val: '00'},
        {text: 'Readout Manual', val: '01'},
        {text: 'Readout Inductive', val: '02'},
        {text: 'Readout Radio', val: '03'},
        {text: 'Readout CL', val: '04'},
        {text: 'Readout PC', val: '05'},
        {text: 'Import File', val: '06'},
        {text: 'Not At Home', val: '10'},
        {text: 'Card Provided', val: '11'},
        {text: 'Dismounted', val: '58'},
        {text: 'Mounted', val: '59'},
      ],
      headers: {
        updated_at: {
          label: 'updated_at',
          order: true,
        },
        customer_name: {
          label: 'client',
          order: true,
        },
        meter_serial_number: {
          label: 'meter_serial',
          order: true,
        },
        /*   obis_code: {
             label: "obis_code",
             order: false,
           },*/
        state: {
          label: 'state',
          order: true,
        },
        value: {
          label: 'volume',
          order: true,
        },
        details: {
          label: '',
          order: false,
          className: '',
        },
        // mapClicker: {
        //   label: "",
        //   order: false,
        //   className: "",
        // },
      },
      /*fields: {
        street: 'Address',
        customer_name: 'Customer Name',
        meter_serial_number: 'Meter Serial',
        obis_code: 'Obis Code',
        state: 'State',
        last_value: 'Volume',
      },*/
    }
  },

  watch: {
    selectedTourItem: {
      immediate: true,
      handler(n) {
        if (n) {
          //console.log("n = "+Object.values(n))
          this.clickedMapPin = n.item_number - 1
          //console.log("clickedMapPin "+this.clickedMapPin)
        }
      },
    },
    async meterSerial(n, o) {
      if (this.mobile) {
        return
      }
      await this.$store.dispatch('app/filterProp', {value: n, name: 'meter_serial'})
    },
    async itemStateFilter(n, o) {
      if (this.mobile) {
        return
      }
      await this.$store.dispatch('app/filterProp', {value: n, name: 'state'})
      console.log(n)
    },
  },
  computed: {
    ...mapGetters([
      {
        getTourDetailsVisibility: 'tours/getTourDetailsVisibility',
        getSelectedTour: 'tours/getSelectedTour',
      },
    ]),
    count() {
      return this.tourItemList && this.tourItemList.count ? this.tourItemList.count : 0
    },
    next() {
      return this.tourItemList && this.tourItemList.next ? this.tourItemList.next : null
    },
    previous() {
      return this.tourItemList && this.tourItemList.previous ? this.tourItemList.previous : null
    },
    results() {
      return this.tourItemList && this.tourItemList.results
        ? Object.freeze(this.tourItemList.results)
        : []
    },
    filterButton() {
      return this.itemStateFilter.length === 0
        ? 'Filter by State'
        : this.whatStateIs(this.itemStateFilter[0]) +
            ` ${this.itemStateFilter.length > 1 ? `+${this.itemStateFilter.length - 1}` : ''}`
    },
    // filterTooltip(){
    //   return this.itemStateFilter.length>1?
    // }
  },
  methods: {
    async exportTour(tour) {
      console.log(tour.uuid)
      console.log('Exporting...')
      const uuid = tour.uuid
      this.isExporting = uuid
      try {
        const file = await this.$store.dispatch('tours/exportTour', uuid)
        const d = new Date()
        const extension =
          Object.keys(this.$store.getters['tours/supportedImportFormats']).find(
            key => this.$store.getters['tours/supportedImportFormats'][key] === file.type
          ) || 'txt'

        const filename = `${tour.name}-${d.toISOString().substring(0, 10)}.${extension}`
        fileExport(file, filename)
        this.$store.dispatch('app/notification', {
          type: 'success',
          group: 'app',
          text: `download`,
        })
        //await this.getTours()
      } catch (error) {
        console.log(error)
      } finally {
        this.isExporting = ''
      }
    },
    showTooltip(values) {
      let tooltip = ''
      if (!values.length) {
        tooltip = 'No filters selected'
      }
      for (let i = 0; i < values.length; i++) {
        tooltip += i + 1 + '.  ' + this.whatStateIs(values[i]) + '<br>'
      }
      return tooltip
    },

    loadMap(visible) {
      this.loaded = visible
    },
    async onSubmit() {
      if (this.pendingRequest) {
        return
      }
      try {
        this.pendingRequest = true
        const id = this.$route.params.id
        let payload = {value: this.volume}
        await this.$store.dispatch('tours/updateTourVolume', {
          uuid: id,
          row: this.row.uuid,
          regId: this.row.registers[0].id,
          payload,
        })
      } catch (err) {
        console.log('An error occurred' + err.toString())
        this.$store.dispatch('app/notification', {
          type: 'error',
          group: 'app',
          text: err.toString(),
        })
      } finally {
        this.pendingRequest = false
        this.getTourItems()
        this.isModalVisible = false
        this.volume = ''
      }
    },
    handleCancel(e) {
      //console.log('Clicked cancel button');
      this.isModalVisible = false
    },

    setValue(item) {
      this.row = {...item}
      this.volume = item.registers[0].value
      this.isModalVisible = true
    },

    onClose() {
      this.$router.push(this.tourListUrl)
    },

    showExpanded(tourItemRow) {
      if (!this.visibleTourItemDetails.includes(tourItemRow.uuid)) {
        return this.visibleTourItemDetails.push(tourItemRow.uuid)
      }
      return this.visibleTourItemDetails.splice(
        this.visibleTourItemDetails.indexOf(tourItemRow.uuid),
        1
      )
    },

    handlePinClick(tourData) {
      //console.log("tourData "+Object.keys(tourData))
      // const recordID = tourData.item_number
      //console.log("recordID "+recordID)
      this.selectedTourItem = tourData
      //console.log("selectedTourItem "+Object.keys(this.selectedTourItem))
    },

    showDataOrNot(data) {
      if (data) return data
      return '-'
    },

    async getTourItems() {
      // console.log("??" + this.tour.uuid)
      this.startPopulating = false
      this.visibleTourItemDetails = []
      const TourItemResponse = await this.$store.dispatch('tours/getTourItemsFull', {
        uuid: this.$route.params.id,
      })
      this.tourItems = TourItemResponse.results
      this.tourItemList = TourItemResponse
      this.startPopulating = true
    },

    /*    showChildrenDrawer(details) {
      this.clickedTourDetails = details
      this.childrenDrawerVisibility = true
    },

    onChildrenDrawerClose() {
      this.childrenDrawerVisibility = false
      this.clickedTourDetails = {}
    },*/
    formatDate(someDate) {
      const aDate = new Date(someDate)
      return aDate.toString() != 'Invalid Date'
        ? aDate.toLocaleString('en-GB', {timeZone: 'UTC'})
        : someDate
    },
    whatStateIs(stateValue) {
      switch (stateValue) {
        case '00':
          return 'Not Readout'
        case '01':
          return 'Readout Manual'
        case '02':
          return 'Readout Inductive'
        case '03':
          return 'Readout Radio'
        case '04':
          return 'Readout CL'
        case '05':
          return 'Readout PC'
        case '06':
          return 'Import File'
        case '10':
          return 'Not At Home'
        case '11':
          return 'Card Provided'
        case '58':
          return 'Dismounted'
        case '59':
          return 'Mounted'
      }
      return 'Undefined'
    },
  },
}
</script>
